import { Layout, navbarLink, useLocationByPath, useMountEffect, clientApi, LoadingSpinner } from "shared"
import { useState } from "react"
import type { Client, CurrentLink } from "shared"
import type { ClientNote, ClientProps } from "Utils/client.types"
import { AccountPage } from "../Account/AccountPage"
import { TeamPage } from "../TeamPage/TeamPage"
import { OptionsPage } from "../Options/OptionsPage"
import { SuperUserLoginPage } from "../SuperUserLogin/SuperUserLogin"
import { VolumePage } from "../Volume/Volume"
import { IntegrationsPage } from "../Integrations/IntegrationsPage"
import { TagsPage } from "../TagsPage/TagsPage"
import { AnalyticsPage } from "../Analytics/Analytics"
import { DeletePage } from "../DeletePage/DeletePage"
import { MigrationPage } from "../MigrationPage/MigrationPage"
import { TemplatesPage } from "../TemplatesPage/TemplatesPage"
import { ClientNotesPage } from "../ClientNotes/ClientNotes"
import EvoIcon from "shared/src/images/EvoIcon.png"

export function ClientDetailPage(): JSX.Element {
	const { clientId, currentLink } = useLocationByPath()
	const [loading, setLoading] = useState<boolean>(true)
	const [data, setData] = useState<ClientProps>()

	const query = {
		attributes: [
			"userlist",
			"stats",
			"paymentType",
			"credits",
			"billingDate",
			"freeTrialEnd",
			"options",
			"outputData",
			"apiKey",
			"secret",
			"externalSecrets",
			"externalId",
			"limits",
			"productType",
			"subdomain",
			"ssoEnabled",
			"evoRef",
			"notes",
			"disabled",
			"cuCode",
		],
	}

	useMountEffect((mounted) => {
		const fetchClients = async () => {
			setLoading(true)
			const response: Partial<Client & { notes: ClientNote[] }> = await clientApi.get({ clientId, query })
			if (mounted.current) {
				const clientProps = { ...response, evoEnabled: response.evoRef != null } as ClientProps
				setData(clientProps)
				setLoading(false)
			}
		}
		fetchClients()
	})

	const badge = data?.evoEnabled ? <img src={EvoIcon} alt="Evo" style={{ height: 25 }} /> : null

	return (
		<>
			{loading && <LoadingSpinner isLoading={loading} />}
			{!loading && data && (
				<Layout
					navbarLink={navbarLink}
					headerTitle={data?.sk}
					clientId={clientId}
					currentLink={currentLink}
					badge={badge}>
					<NoteBanner data={data} />
					<Tab currentLink={currentLink} clientId={clientId} data={data} setData={setData} />
				</Layout>
			)}
		</>
	)
}

const Tab = ({
	currentLink,
	clientId,
	data,
	setData,
}: {
	currentLink: CurrentLink
	clientId: string
	data: object
	setData?: (data: object) => void
}): JSX.Element => {
	switch (currentLink) {
		case "account":
			return <AccountPage data={data} clientId={clientId} setData={setData} />
		case "login":
			return <SuperUserLoginPage data={data} clientId={clientId} />
		case "templates":
			return <TemplatesPage clientId={clientId} data={data} setData={setData} />
		case "team":
			return <TeamPage clientId={clientId} data={data} setData={setData} />
		case "options":
			return <OptionsPage clientId={clientId} data={data} setData={setData} />
		case "integrations":
			return <IntegrationsPage clientId={clientId} data={data} setData={setData} />
		case "tags":
			return <TagsPage clientId={clientId} data={data} setData={setData} />
		case "volume":
			return <VolumePage clientId={clientId} data={data} />
		case "analytics":
			return <AnalyticsPage clientId={clientId} data={data} />
		case "notes":
			return <ClientNotesPage clientId={clientId} data={data} />
		case "migration":
			return <MigrationPage clientId={clientId} data={data} setData={setData} />
		case "delete":
			return <DeletePage clientId={clientId} data={data} setData={setData} />
		default:
			return <div>404</div>
	}
}

interface NoteBannerProps {
	data: ClientProps
}

const NoteBanner = ({ data }: NoteBannerProps): JSX.Element | null => {
	const [dismissed, setDismissed] = useState(false)

	if (!data.notes || data.notes.length === 0 || dismissed) {
		return null
	}

	// Filter non-expired notes
	const currentTimestamp = Math.floor(Date.now() / 1000)
	const activeNotes = data.notes.filter((note) => note.expiry > currentTimestamp)

	if (activeNotes.length === 0) {
		return null
	}

	return (
		<div
			style={{
				backgroundColor: "#fff3cd",
				border: "1px solid #ffeaa7",
				borderRadius: 4,
				padding: 12,
				margin: "0 0 20px 0",
				position: "relative",
			}}>
			<button
				onClick={() => setDismissed(true)}
				style={{
					position: "absolute",
					top: 8,
					right: 8,
					background: "none",
					border: "none",
					fontSize: 18,
					cursor: "pointer",
					color: "#856404",
					lineHeight: 1,
					padding: 4,
				}}
				title="Dismiss notes">
				×
			</button>
			<div style={{ paddingRight: 30 }}>
				<div style={{ fontSize: 14, fontWeight: "bold", color: "#856404", marginBottom: 8 }}>
					Active Notes ({activeNotes.length})
				</div>
				{activeNotes.map((note, index) => (
					<div
						key={index}
						style={{
							padding: "8px 0",
							borderBottom: index < activeNotes.length - 1 ? "1px solid #ffeaa7" : "none",
						}}>
						<div style={{ color: "#856404", marginBottom: 4 }}>{note.text}</div>
						<div style={{ fontSize: 12, color: "#996600" }}>
							Expires: {new Date(note.expiry * 1000).toLocaleString()}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}
